export const COOKIEYES_BANNER_SELECTOR =
  '.cky-consent-container, .cky-modal, .cky-btn-revisit-wrapper'

export const COOKIEYES_BANNER_STYLE = `
.cky-consent-container,
.cky-modal,
.cky-btn-revisit-wrapper {
  pointer-events: auto;
}
`

const MODAL_SELECTOR = '[role="dialog"], [role="alertdialog"]'

const MODAL_ARIA_HIDDEN_MARKER = 'data-aria-hidden'

const BANNER_EVENTS = [
  'pointerdown',
  'focusin',
  'wheel',
  'touchstart',
  'touchmove',
] as const

let isInstalled = false
let trappingDialog: HTMLElement | null = null

function isInBanner(target: EventTarget | null): boolean {
  return (
    target instanceof Element && !!target.closest(COOKIEYES_BANNER_SELECTOR)
  )
}

function isModalOpen(): boolean {
  return document.body.style.pointerEvents === 'none'
}

function isHidden(element: Element): boolean {
  return (
    element.getClientRects().length === 0 ||
    getComputedStyle(element).visibility === 'hidden'
  )
}

function getTabbables(container: Element): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>('*')).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !(element as HTMLButtonElement).disabled &&
      !(element instanceof HTMLInputElement && element.type === 'hidden') &&
      !isHidden(element),
  )
}

function getBannerTabbables(): HTMLElement[] {
  return Array.from(document.querySelectorAll(COOKIEYES_BANNER_SELECTOR))
    .filter((banner) => !isHidden(banner))
    .flatMap(getTabbables)
}

function moveFocus(
  event: KeyboardEvent,
  target: HTMLElement | undefined,
): void {
  if (!target) return
  event.preventDefault()
  target.focus()
}

function revealBannerToAssistiveTech(mutations: MutationRecord[]): void {
  for (const { target } of mutations) {
    if (
      target instanceof Element &&
      target.matches(COOKIEYES_BANNER_SELECTOR) &&
      target.hasAttribute(MODAL_ARIA_HIDDEN_MARKER)
    ) {
      target.removeAttribute('aria-hidden')
    }
  }
}

function onBannerEvent(event: Event): void {
  if (isInBanner(event.target)) event.stopPropagation()
}

function onFocusOut(event: FocusEvent): void {
  if (isInBanner(event.relatedTarget)) event.stopPropagation()
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && isModalOpen() && isInBanner(event.target)) {
    event.preventDefault()
    return
  }
  if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey)
    return
  if (!isModalOpen()) return

  const active = document.activeElement
  if (!(active instanceof HTMLElement)) return

  const bannerTabbables = getBannerTabbables()
  if (bannerTabbables.length === 0) return

  if (isInBanner(active)) {
    if (!trappingDialog?.isConnected) return
    const dialogTabbables = getTabbables(trappingDialog)
    if (!event.shiftKey && active === bannerTabbables.at(-1)) {
      moveFocus(event, dialogTabbables[0])
    } else if (event.shiftKey && active === bannerTabbables[0]) {
      moveFocus(event, dialogTabbables.at(-1))
    }
    return
  }

  const dialog = active.closest<HTMLElement>(MODAL_SELECTOR)
  if (!dialog) return
  const dialogTabbables = getTabbables(dialog)
  const isLeavingForward = !event.shiftKey && active === dialogTabbables.at(-1)
  const isLeavingBackward = event.shiftKey && active === dialogTabbables[0]
  if (!isLeavingForward && !isLeavingBackward) return

  trappingDialog = dialog
  moveFocus(
    event,
    isLeavingForward ? bannerTabbables[0] : bannerTabbables.at(-1),
  )
}

export function keepCookieBannerUsableOverModals(): void {
  if (isInstalled) return
  isInstalled = true

  for (const type of BANNER_EVENTS) {
    document.body.addEventListener(type, onBannerEvent, { passive: true })
  }
  document.body.addEventListener('focusout', onFocusOut)
  window.addEventListener('keydown', onKeyDown, { capture: true })
  new MutationObserver(revealBannerToAssistiveTech).observe(document.body, {
    attributes: true,
    attributeFilter: ['aria-hidden', MODAL_ARIA_HIDDEN_MARKER],
    subtree: true,
  })
}
